import * as THREE from 'three'
import { EditorUserError } from '@glass-designer/utils/editorErrors'

import BaseTool from '@glass-designer/tools/baseTool'
import MathsUtils from '@/utils/mathsUtils'
import LinearRulerObject from '@glass-designer/objects/linearRulerObject'
import CircleShape from '@glass-designer/model/shapes/circleShape'
import OvalShape from '@glass-designer/model/shapes/ovalShape'

import GlassDesignerTypes from '@glass-designer/types'

// Dimension Edit tool
// Tool used when dimension value clicked to edit the length / angle of a segment
export default class DimensionEditTool extends BaseTool {
  constructor(editor) {
    super(editor)

    this.type = GlassDesignerTypes.ToolTypes.dimensionEditTool
    this.selectedShape = null
    this.selectedSegmentIndex = 0
    this.selectedHole = null
    this.selectedDimension = null
    this.selectedDimensionStartOffset = 0
    this.selectedDimensionEndOffset = 0
    this.selectedScaleEditIsWidth = false
    this.datumLinkId = null

    this.originalLength = 0
    this.originalDimAngle = 0

    this.linearRulerObj = new LinearRulerObject(this.editor, {
      colour: '#FF0000',
      displayAsChange: true
    })
    this.linearRulerObj.setVisible(false)
    this.linearRulerObj.setObjectEventListener(this)
    this.addChildObject(this.linearRulerObj)
  }


  // Tool Active
  // Called when the tool is active
  onToolActive(options = {}) {
    super.onToolActive()

    this.selectedShape = null
    this.selectedHole = null
    this.selectedDimension = null

    // Reset
    this.selectedDimensionStartOffset = 0
    this.selectedDimensionEndOffset = 0

    // Segment Dimension Editing
    if(options.dimension.dimType == GlassDesignerTypes.DimensionTypes.linearGlassSegmentDimension) {
      this.selectedShape = options.dimension.glassShape
      this.selectedSegmentIndex = options.dimension.glassSegmentIndex
      this.selectedDimension = options.dimension
      let dimLinkData = options.dimension.linkData || {}

      let segment = this.selectedShape.getSegmentForIndex(this.selectedSegmentIndex)
      var segmentDegrees = MathsUtils.radiansToDegrees(segment.getAngle())
      var extendAngle = MathsUtils.roundNumber(segmentDegrees / 45.0) * 45.0      // Snap to nearest 45 degrees for visual appearance

      let dimensionLength = segment.getLength()

      // Apply offsets if linked to shape groups at start/end
      if(dimLinkData.startShapeGroupId) {
        this.selectedDimensionStartOffset = dimLinkData.startGroupOffset || 0
        dimensionLength += dimLinkData.startGroupOffset || 0
      }
      if(dimLinkData.endShapeGroupId) {
        this.selectedDimensionEndOffset = dimLinkData.endGroupOffset || 0
        dimensionLength += dimLinkData.endGroupOffset || 0
      }

      // Display an indicator on the start/end of the segment showing the change difference
      this.linearRulerObj.setStartPosition(segment.endPosition)
      this.linearRulerObj.setEndPosition(segment.endPosition)
      this.linearRulerObj.setOutDirection(segment.getOutDirection())
      this.linearRulerObj.updateSceneObject()


      // Fill in options
      let popoverOptions = {
        value: dimensionLength,
        displayUserFixedField: true,
        displayExtendDirection: true,
        extendDirectionAngle: extendAngle,
        isUserFixed: segment.isUserFixed,
        allowPlusMinus: false,
        title: 'Length',
        addon: 'mm',
        context: this,
        updateCallback: this.updateShapeEdit,
        cancelCallback: this.cancelShapeEdit,
        applyCallback: this.applyShapeEdit
      }
      let displayOptions = {
        horizontalPosition: this._calculatePopoverHorizontalPosition(segment.calculateBounds())
      }

      this.editor.component.displayNumberEntryPopover(popoverOptions, options.position, displayOptions)

      this.editor.startShapeEditingMode(this.selectedShape)
    }

    // Shape Scaling via Overall Dimension Editing
    if(options.dimension.dimType == GlassDesignerTypes.DimensionTypes.linearGlassOverallDimension) {
      this.selectedShape = options.dimension.glassShape
      this.selectedDimension = options.dimension

      let shapeBounds = this.selectedShape.calculateSegmentBounds()

      // Check if scaling width or height of the shape
      this.selectedScaleEditIsWidth = false
      if(Math.abs(this.selectedDimension.lineDirection.x) > Math.abs(this.selectedDimension.lineDirection.y))
        this.selectedScaleEditIsWidth = true

      let dimensionLength = shapeBounds.max.y - shapeBounds.min.y
      if(this.selectedScaleEditIsWidth)
        dimensionLength = shapeBounds.max.x - shapeBounds.min.x

      // Fill in options
      let popoverOptions = {
        value: dimensionLength,
        displayUserFixedField: false,
        displayExtendDirection: false,
        allowPlusMinus: false,
        title: `Panel ${this.selectedScaleEditIsWidth ? 'Width' : 'Height'}`,
        addon: 'mm',
        context: this,
        updateCallback: (ctx, options) => {
          ctx.updateShapeScaleEdit(options)
        },
        cancelCallback: (ctx) => {
          ctx.cancelShapeScaleEdit()
        },
        applyCallback: (ctx, options) => {
          ctx.applyShapeScaleEdit(options)
        }
      }
      let displayOptions = {
        horizontalPosition: this._calculatePopoverHorizontalPosition(shapeBounds)
      }

      this.editor.component.displayNumberEntryPopover(popoverOptions, options.position, displayOptions)
      this.editor.startShapeEditingMode(this.selectedShape)
    }

    // Hole Offset Editing
    if(options.dimension.dimType == GlassDesignerTypes.DimensionTypes.glassHoleOffsetDimension) {
      this.selectedShape = options.dimension.glassShape
      this.selectedHole = options.dimension.glassHole
      this.selectedDimension = options.dimension
      this.datumLinkId = (options.dimension.linkData || {}).datumLinkId

      // Store all info for the tools below
      var shapeId = this.selectedShape.id
      var holeId = this.selectedHole.id

      let datumLink = this.selectedHole.getDatumLinkById(this.datumLinkId)
      if(datumLink) {

        this.originalLength = datumLink.sourceOffsetDistance
        this.originalDimAngle = datumLink.calculateSourceAngle()

        // Fill in options
        let popoverOptions = {
          value: datumLink.sourceOffsetDistance,
          displayUserFixedField: false,
          displayExtendDirection: false,
          displayDimensionSourceUpdate: true,
          displayDimensionAngleUpdate: datumLink.hasAngleOption(),
          dimensionAngle: datumLink.calculateSourceAngle(),
          allowPlusMinus: false,
          title: 'Length',
          addon: 'mm',
          context: this,
          updateCallback: this.updateHoleOffsetEdit,
          cancelCallback: this.cancelHoleOffsetEdit,
          applyCallback: this.applyHoleOffsetEdit,
          actionCallback: (ctx, updateType, info) => {
            switch(updateType) {
              case 'changeDatum':
                // Start datum change tool
                ctx.restoreHoleOffsetEdit()
                ctx.editor.startTool(GlassDesignerTypes.ToolTypes.dimensionEditDatumTool, {
                  shapeId: shapeId,
                  holeId: holeId,
                  datumLinkId: datumLink.id
                })
                break
            }
          }
        }

        let displayOptions = {
          horizontalPosition: this._calculatePopoverHorizontalPosition(this.selectedHole.calculateSegmentBounds())
        }

        this.editor.component.displayNumberEntryPopover(popoverOptions, options.position, displayOptions)
      }
    }
  }

  // Tool Inactive
  // Called when the tool is no longer active
  onToolInactive() {
    super.onToolInactive()

    this.linearRulerObj.setVisible(false)

    // Cancel any editing
    this.restoreHoleOffsetEdit()
    this.editor.endShapeEditingMode()
    this.editor.component.hidePopover()
  }


  updateShapeEdit(context, options) {
    let maxLength = 10000   // Sanity check

    if(options.value <= 0 && options.usingInputField)
      throw new EditorUserError('Invalid dimension value. Must be greater than zero')
    if(options.value > maxLength)
      throw new EditorUserError(`Length is too large. Must be less than ${maxLength}`)

    let dimensionGroupOffset = context.selectedDimensionStartOffset + context.selectedDimensionEndOffset

    context.editor.updateShapeSegmentDimension(context.selectedShape, context.selectedSegmentIndex, {
      highlightOnly: true,
      length: options.value - dimensionGroupOffset,
      isUserFixed: options.isUserFixed,
      updateUserFixed: options.updateUserFixed,
      extendDirectionIndex: options.extendDirectionIndex
    })

    let originalSegment = context.selectedShape.getSegmentForIndex(context.selectedSegmentIndex)
    let highlightSegment = context.editor.highlightShape.getSegmentForIndex(context.selectedSegmentIndex)

    if(options.extendDirectionIndex == 0) {
      context.linearRulerObj.setStartPosition(originalSegment.startPosition)
      context.linearRulerObj.setEndPosition(highlightSegment.startPosition)
    } else {
      context.linearRulerObj.setStartPosition(originalSegment.endPosition)
      context.linearRulerObj.setEndPosition(highlightSegment.endPosition)
    }
    let rulerDistance = originalSegment.getLength() - highlightSegment.getLength()
    context.linearRulerObj.setMeasurementPositive(rulerDistance < 0)
    context.linearRulerObj.setVisible(Math.abs(Math.round(rulerDistance)) > 0)
    context.linearRulerObj.updateSceneObject()
    
    context.editor.render()
  }

  applyShapeEdit(context, options) {
    let maxLength = 10000   // Sanity check

    if(options.value <= 0)
      throw new EditorUserError('Invalid dimension value. Must be greater than zero')
    if(options.value > maxLength)
      throw new EditorUserError(`Length is too large. Must be less than ${maxLength}`)

    let dimensionGroupOffset = context.selectedDimensionStartOffset + context.selectedDimensionEndOffset

    context.editor.updateShapeSegmentDimension(context.selectedShape, context.selectedSegmentIndex, {
      highlightOnly: false,
      length: options.value - dimensionGroupOffset,
      isUserFixed: options.isUserFixed,
      updateUserFixed: options.updateUserFixed,
      extendDirectionIndex: options.extendDirectionIndex
    })
  }

  cancelShapeEdit(context) {
    context.editor.cancelActiveTool()
  }


  // Shape Scaling Dimension Edit
  updateShapeScaleEdit(options) {
    let maxLength = 15000   // Sanity check

    if(options.value <= 0 && options.usingInputField)
      throw new EditorUserError('Invalid dimension value. Must be greater than zero')
    if(options.value > maxLength)
      throw new EditorUserError(`Length is too large. Must be less than ${maxLength}`)

    
    this.editor.resetHighlightShape(this.selectedShape)
    let updateShape = this.editor.highlightShape

    // Resize shape
    let moveDir = new THREE.Vector2(this.selectedScaleEditIsWidth ? 1 : 0, this.selectedScaleEditIsWidth ? 0 : 1)
    this._resizeShapeSize(updateShape, moveDir, options.value)
   
    this.editor.render()
  }
  applyShapeScaleEdit(options) {
    let maxLength = 15000   // Sanity check

    if(options.value <= 0 && options.usingInputField)
      throw new EditorUserError('Invalid dimension value. Must be greater than zero')
    if(options.value > maxLength)
      throw new EditorUserError(`Length is too large. Must be less than ${maxLength}`)

    try {
      this.editor.startUndoGroup('Resize panel')
      this.editor.addUndoShape(this.selectedShape)

      // Resize shape
      let moveDir = new THREE.Vector2(this.selectedScaleEditIsWidth ? 1 : 0, this.selectedScaleEditIsWidth ? 0 : 1)
      this._resizeShapeSize(this.selectedShape, moveDir, options.value)

      this.editor.closeCurrentUndoGroup()
    } catch(err) {
      this.editor.cancelCurrentUndoGroup()
      throw err
    }

    this.selectedShape = null
    this.closeTool()
   
    this.editor.render()
  }
  cancelShapeScaleEdit() {
    this.editor.cancelActiveTool()
  }


  /**
   * Resize a shape in a particular direction to meet a target width/height
   */
  _resizeShapeSize(shape, moveDir, targetSize) {
    let shapeBounds = shape.calculateSegmentBounds()
    let boundCenter = shapeBounds.getCenter(new THREE.Vector2())

    let isWidthResize = Math.abs(moveDir.x) > Math.abs(moveDir.y)

    let moveRequired = isWidthResize ? (targetSize - (shapeBounds.max.x - shapeBounds.min.x)) : (targetSize - (shapeBounds.max.y - shapeBounds.min.y))
    let moveDirType = GlassDesignerTypes.MoveEditDirectionTypes.down
    if(isWidthResize)
      moveDirType = GlassDesignerTypes.MoveEditDirectionTypes.right

    // If an oval/circle shape, modify the properties & rebuild the shape
    // Only do if the shape layout has not been modified. Once that happens, we do a custom shape style resize
    if(shape instanceof CircleShape) {
      // Check if the shape still forms a circle
      if(shape.isLayoutCircleShape()) {
        shape.resizeShape(targetSize / 2)   // Radius is half the full width/height
        shape.segmentListUpdated()
        shape.updateSceneObject()
        return
      }
    }
    if(shape instanceof OvalShape) {
      // Check if the shape still forms an oval
      if(shape.isLayoutOvalShape()) {
        if(isWidthResize)
          shape.resizeShape(targetSize, shape.height)
        else
          shape.resizeShape(shape.width, targetSize)

        shape.segmentListUpdated()
        shape.updateSceneObject()
        return
      }
    }

    let segmentStartInfoList = []
    shape.segmentList.forEach((segment, segmentIndex) => {
      if(
        (segment.startPosition.y > boundCenter.y && !isWidthResize) ||
        (segment.startPosition.x > boundCenter.x && isWidthResize)) {

        segmentStartInfoList.push({
          index: segmentIndex,
          targetDist: MathsUtils.calculateDistanceToPlane2D(moveDir, boundCenter, segment.startPosition.clone().addScaledVector(moveDir, moveRequired)),
          startPosition: segment.startPosition.clone()
        })
      }
    })

    segmentStartInfoList.forEach(segmentInfo => {
      let segment = shape.getSegmentForIndex(segmentInfo.index)

      let dist = MathsUtils.calculateDistanceToPlane2D(moveDir, boundCenter, segment.startPosition)

      //if(dist < segmentInfo.targetDist) {
        shape.moveSegmentStart(segmentInfo.index, segmentInfo.targetDist - dist, moveDirType)
      //}
    })

    shape.segmentListUpdated()
    shape.updateSceneObject()
  }


  updateHoleOffsetEdit(context, options) {
    let maxLength = 10000   // Sanity check

    if(options.value <= 0 && options.usingInputField)
      throw new EditorUserError('Invalid dimension value. Must be greater than zero')
    if(options.value > maxLength)
      throw new EditorUserError(`Length is too large. Must be less than ${maxLength}`)

    let datumLink = context.selectedHole.getDatumLinkById(context.datumLinkId)

    if(datumLink) {
      datumLink.sourceOffsetDistance = options.value

      if(datumLink.hasAngleOption())
        datumLink.sourceAngleOffset = options.dimensionAngle

      context.selectedShape.internalItemsUpdated()
      context.editor.onShapeUpdated(context.selectedShape)
    }
  }

  applyHoleOffsetEdit(context, options) {
    try {
      // TODO: This isn't going to work, the datum is already updated & undo group is going to store that
      // TODO: Use highlighted objects to display hole move & apply actual update here

      context.editor.startUndoGroup('Edit cutout position')
      context.editor.addUndoShape(context.selectedShape)

      context.updateHoleOffsetEdit(context, options)

      context.editor.closeCurrentUndoGroup()
    } catch(err) {
      context.editor.cancelCurrentUndoGroup()
      throw err
    }

    context.selectedHole = null
    context.closeTool()
  }

  cancelHoleOffsetEdit(context, userCancelled) {
    // Restore offset to original length
    if(userCancelled) {
      context.restoreHoleOffsetEdit()
      context.closeTool()
    }
  }

  restoreHoleOffsetEdit() {
    if(!this.selectedHole)
      return

    let datumLink = this.selectedHole.getDatumLinkById(this.datumLinkId)
    if(datumLink) {
      datumLink.sourceOffsetDistance = this.originalLength

      if(datumLink.hasAngleOption())
        datumLink.sourceAngleOffset = this.originalDimAngle

      this.selectedShape.internalItemsUpdated()
      this.editor.onShapeUpdated(this.selectedShape)
    }

    this.selectedHole = null
  }
}
